import { useUpdateSubscription } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { useExistingSubscription } from "@/hooks/useExistingSubscription";
import Sentry from "@/integrations/Sentry";
import {
  trackBillingCheckoutCompleted,
  trackBillingCheckoutFailed,
} from "@/utils/analytics";
import {
  buildPaymentMethodUpdate,
  paymentMethodIdFrom,
  type PaymentMethodIntent,
} from "@/utils/confirmSetup";
import type { BillingInterval } from "@cohesive/api-client";
import { ErrorDisplay, Spinner } from "@cohesive/ui-components";
import { useStripe } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

interface ConfirmSetupParams {
  stripe: Stripe;
  setupIntentClientSecret: string;
  intent: PaymentMethodIntent;
  redirectTo: string | null;
  hasExistingSubscription: boolean;
  existingSubscriptionStatus: string | undefined;
}

export const ConfirmSetupPage: React.FC = () => {
  const stripe = useStripe();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateSubscription = useUpdateSubscription();
  const queryClient = useQueryClient();

  const { data: userInfo } = useUserInfo();
  const {
    hasExistingSubscription,
    existingSubscriptionStatus,
    isLoading: isUserInfoLoading,
  } = useExistingSubscription();

  // Create a mutation for the confirm setup flow
  // This ensures the logic only runs once and provides proper loading/error states
  const confirmSetupMutation = useMutation({
    mutationKey: ["confirmSetup", searchParams.get("setup_intent")],
    mutationFn: async (params: ConfirmSetupParams) => {
      const { stripe, setupIntentClientSecret, intent } = params;

      const { setupIntent } = await stripe.retrieveSetupIntent(
        setupIntentClientSecret,
      );
      const paymentMethodId = paymentMethodIdFrom(setupIntent);
      if (!paymentMethodId) {
        throw new Error("Invalid payment method");
      }

      const wasTrialing = params.existingSubscriptionStatus === "trialing";
      const payload = buildPaymentMethodUpdate({
        intent,
        paymentMethodId,
        hasExistingSubscription: params.hasExistingSubscription,
        existingSubscriptionStatus: params.existingSubscriptionStatus,
      });

      if (payload) {
        await updateSubscription.mutateAsync(payload);
      } else {
        // The card attached at Stripe but nothing made it the default. Show it
        // rather than hide it.
        await queryClient.invalidateQueries({
          queryKey: ["paymentMethods"],
          refetchType: "all",
        });
      }

      return { wasTrialing };
    },
    onSuccess: (data, params) => {
      const { intent, redirectTo } = params;

      // Track successful checkout completion
      trackBillingCheckoutCompleted({
        planName: intent.planName || undefined,
        billingInterval: intent.billingCycle || undefined,
        seatCount: intent.userCount || undefined,
        isPaymentMethodUpdate: intent.isPaymentMethodUpdate,
        wasTrialing: data.wasTrialing,
        organization_id: userInfo?.organization?.id,
      });

      // Navigate to specified redirect or default to HOME
      navigate(redirectTo || ROUTES.HOME, { replace: true });
    },
    onError: (error: Error, params) => {
      const { intent } = params;

      // Track checkout failure
      trackBillingCheckoutFailed({
        planName: intent.planName || undefined,
        billingInterval: intent.billingCycle || undefined,
        seatCount: intent.userCount || undefined,
        error: error.message,
        isPaymentMethodUpdate: intent.isPaymentMethodUpdate,
        organization_id: userInfo?.organization?.id,
      });

      Sentry.captureException(error);
    },
  });

  // Parse search params once
  const setupIntent = searchParams.get("setup_intent");
  const setupIntentClientSecret = searchParams.get(
    "setup_intent_client_secret",
  );
  const isPaymentMethodUpdate =
    searchParams.get("mode") === "payment_method_update";
  const keepTrial = searchParams.get("keep_trial") === "1";
  const redirectTo = searchParams.get("redirect_to");
  const planName = searchParams.get("plan");
  const billingCycle = searchParams.get(
    "billing_cycle",
  ) as BillingInterval | null;
  const userCount = parseInt(searchParams.get("user_count") || "1");

  // Trigger the mutation once when all required data is available
  useEffect(() => {
    // Don't proceed until we have the required data
    if (
      !stripe ||
      !setupIntent ||
      !setupIntentClientSecret ||
      isUserInfoLoading
    ) {
      return;
    }

    // Don't trigger if mutation is already in progress, succeeded, or failed
    if (
      confirmSetupMutation.isPending ||
      confirmSetupMutation.isSuccess ||
      confirmSetupMutation.isError
    ) {
      return;
    }

    // Trigger the mutation
    confirmSetupMutation.mutate({
      stripe,
      setupIntentClientSecret,
      intent: {
        planName,
        billingCycle,
        userCount,
        isPaymentMethodUpdate,
        keepTrial,
      },
      redirectTo,
      hasExistingSubscription,
      existingSubscriptionStatus,
    });
  }, [
    stripe,
    setupIntent,
    setupIntentClientSecret,
    isUserInfoLoading,
    confirmSetupMutation.isPending,
    confirmSetupMutation.isSuccess,
    confirmSetupMutation.isError,
  ]);

  if (confirmSetupMutation.isError) {
    const title = "Error";
    return (
      <>
        <title>{title}</title>
        <div data-testid="error-message">
          <ErrorDisplay
            title={title}
            message={confirmSetupMutation.error.message}
          />
        </div>
      </>
    );
  }

  const title = isPaymentMethodUpdate
    ? "Updating Payment Method"
    : "Confirming Your Purchase";

  return (
    <>
      <title>{title}</title>
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    </>
  );
};
